/** Minimum distance between a menu and the edge of the viewport, in px. */
export const MENU_EDGE = 8;
/** Gap between trigger and menu, in px (matches `--space-1-5`). */
const MENU_GAP = 6;

export interface MenuPositionInput {
  /** Bounding box of the trigger, in viewport coordinates. */
  anchor: { left: number; right: number; top: number; bottom: number };
  /** Size of the menu, measured with its maximum width already applied. */
  menu: { width: number; height: number };
  viewport: { width: number; height: number };
  align: 'start' | 'end';
  placement: 'bottom' | 'top';
}

/**
 * Where the open menu goes: aligned to the trigger as asked, flipped to the
 * other alignment when it would leave the viewport, and pushed inside the
 * edges when neither fits. The vertical side is the wanted one if the menu
 * fits there, else the side with room, else the larger side; `maxHeight`
 * keeps it on screen so it scrolls.
 */
export function placeMenu({ anchor, menu, viewport, align, placement }: MenuPositionInput) {
  const maxLeft = viewport.width - MENU_EDGE - menu.width;
  const start = anchor.left;
  const end = anchor.right - menu.width;
  const fitsX = (x: number) => x >= MENU_EDGE && x <= maxLeft;
  const [wanted, flipped] = align === 'start' ? [start, end] : [end, start];
  const x = fitsX(wanted) ? wanted : fitsX(flipped) ? flipped : wanted;
  const left = Math.max(MENU_EDGE, Math.min(x, maxLeft));

  const below = viewport.height - anchor.bottom - MENU_GAP - MENU_EDGE;
  const above = anchor.top - MENU_GAP - MENU_EDGE;
  const room = (s: 'bottom' | 'top') => (s === 'bottom' ? below : above);
  const other = placement === 'bottom' ? 'top' : 'bottom';
  const side = room(placement) >= menu.height || room(placement) >= room(other) ? placement : other;

  return { left, side, maxHeight: Math.max(0, room(side)) } as const;
}
